import { useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  JAPAN_PRIORITY_BAND_COLORS,
  rankJapanAssets,
  type JapanCountry,
  type RankedJapanAsset,
} from './japanEarthquakeNotification';
import {
  getJapanSitrepContent,
  type JapanSitrepContent,
  type JapanSitrepLang,
  type JapanSitrepSectionId,
} from './japanSitrepContent';

/** Rows visible before "Show all", sized so the table fits a 1080p capture. */
const ASSETS_PREVIEW_COUNT = 6;
const OSINT_PREVIEW_COUNT = 4;

const RANKED_ASSETS = rankJapanAssets();

function CountryBadge({ country }: { country: JapanCountry }) {
  return (
    <span
      className={cn(
        'tw:inline-flex tw:h-4 tw:shrink-0 tw:items-center tw:rounded tw:px-1 tw:text-[10px] tw:font-semibold tw:leading-none',
        country === 'US'
          ? 'tw:bg-blue-500/20 tw:text-blue-200'
          : 'tw:bg-red-500/20 tw:text-red-200'
      )}
    >
      {country}
    </span>
  );
}

function SectionHeading({
  content,
  id,
  children,
}: {
  content: JapanSitrepContent;
  id: JapanSitrepSectionId;
  children?: ReactNode;
}) {
  const heading = content.sections.find((section) => section.id === id);
  return (
    <div className="tw:mb-1.5 tw:flex tw:items-center tw:gap-2">
      <h4 className="tw:m-0 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        {heading?.heading}
      </h4>
      {children}
    </div>
  );
}

function ShowAllToggle({
  expanded,
  total,
  content,
  onToggle,
}: {
  expanded: boolean;
  total: number;
  content: JapanSitrepContent;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className="tw:mt-1 tw:text-muted-foreground"
      onClick={onToggle}
    >
      {expanded ? content.showLess : content.showAll(total)}
    </Button>
  );
}

function AssetRow({
  entry,
  lang,
  content,
}: {
  entry: RankedJapanAsset;
  lang: JapanSitrepLang;
  content: JapanSitrepContent;
}) {
  const { facility, rank, band } = entry;
  return (
    <tr className="tw:border-t tw:border-border/60 tw:align-top">
      <td className="tw:py-1 tw:pr-2">
        <span
          className={cn(
            'tw:inline-flex tw:size-5 tw:items-center tw:justify-center tw:rounded-full tw:text-[10px] tw:font-semibold',
            band === 'top' ? 'tw:text-white' : 'tw:bg-muted tw:text-foreground'
          )}
          style={
            band === 'top'
              ? { backgroundColor: JAPAN_PRIORITY_BAND_COLORS.top }
              : undefined
          }
        >
          {rank}
        </span>
      </td>
      <td className="tw:py-1 tw:pr-2">
        <div className="tw:flex tw:items-center tw:gap-1">
          <CountryBadge country={facility.country} />
          <span className="tw:font-medium">
            {lang === 'ja' ? facility.nameJa : facility.name}
          </span>
        </div>
        {facility.medical ? (
          <div className="tw:mt-0.5 tw:flex tw:flex-wrap tw:gap-1">
            <Badge
              variant="outline"
              className={cn(
                'tw:h-4 tw:px-1 tw:text-[10px]',
                facility.medical.beds === 'Diverting' &&
                  'tw:border-destructive/50 tw:text-destructive'
              )}
            >
              {content.bedLabels[facility.medical.beds]}
            </Badge>
            <Badge variant="outline" className="tw:h-4 tw:px-1 tw:text-[10px]">
              {facility.medical.decon
                ? content.deconLabels.yes
                : content.deconLabels.no}
            </Badge>
          </div>
        ) : null}
      </td>
      <td className="tw:py-1 tw:pr-2 tw:whitespace-nowrap">
        T{facility.criticalityTier}
      </td>
      <td className="tw:py-1 tw:pr-2 tw:whitespace-nowrap">
        {content.impactLabels[facility.impact]}
      </td>
      <td className="tw:py-1 tw:pr-2 tw:text-muted-foreground">
        {content.statusLabels[facility.operationalStatus]}
      </td>
      <td className="tw:py-1 tw:text-muted-foreground">
        {lang === 'ja' ? facility.rankNoteJa : facility.rankNote}
      </td>
    </tr>
  );
}

/**
 * Demo-only bilingual SITREP for the Japan demo. English and Japanese are both
 * pre-written; the toggle swaps content rather than calling a translator.
 */
export function JapanSitrepPanel() {
  const [lang, setLang] = useState<JapanSitrepLang>('en');
  const [showAllAssets, setShowAllAssets] = useState(false);
  const [showAllOsint, setShowAllOsint] = useState(false);
  const content = getJapanSitrepContent(lang);

  const visibleAssets = showAllAssets
    ? RANKED_ASSETS
    : RANKED_ASSETS.slice(0, ASSETS_PREVIEW_COUNT);
  const visibleOsint = showAllOsint
    ? content.osint
    : content.osint.slice(0, OSINT_PREVIEW_COUNT);

  return (
    <div
      className="tw:space-y-4 tw:rounded-lg tw:border tw:border-border tw:bg-card/60 tw:p-3 tw:text-xs tw:text-foreground"
      data-tour="japan-sitrep"
      lang={lang}
    >
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-2">
        <div>
          <p className="tw:m-0 tw:text-sm tw:font-semibold">{content.title}</p>
          <p className="tw:m-0 tw:text-muted-foreground">{content.asOf}</p>
        </div>
        <div
          className="tw:flex tw:rounded-md tw:border tw:border-border tw:p-0.5"
          role="group"
          aria-label="SITREP language"
          data-tour="japan-sitrep-language"
        >
          {(
            [
              ['en', 'English'],
              ['ja', '日本語'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="xs"
              variant={lang === value ? 'secondary' : 'ghost'}
              aria-pressed={lang === value}
              onClick={() => setLang(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      {content.translatedNote ? (
        <Badge variant="outline" className="tw:text-[10px]">
          {content.translatedNote}
        </Badge>
      ) : null}

      <section data-tour="japan-sitrep-situation">
        <SectionHeading content={content} id="situation" />
        <div className="tw:space-y-1.5">
          {content.situation.map((paragraph) => (
            <p key={paragraph} className="tw:m-0 tw:leading-snug">
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      <section data-tour="japan-sitrep-assets">
        <SectionHeading content={content} id="assets" />
        <p className="tw:m-0 tw:mb-1 tw:text-muted-foreground">
          {content.assetsLegend}
        </p>
        <table className="tw:w-full tw:border-collapse tw:text-left">
          <thead>
            <tr className="tw:text-[10px] tw:uppercase tw:text-muted-foreground">
              <th className="tw:pb-1 tw:pr-2 tw:font-medium">
                {content.assetColumns.rank}
              </th>
              <th className="tw:pb-1 tw:pr-2 tw:font-medium">
                {content.assetColumns.asset}
              </th>
              <th className="tw:pb-1 tw:pr-2 tw:font-medium">
                {content.assetColumns.tier}
              </th>
              <th className="tw:pb-1 tw:pr-2 tw:font-medium">
                {content.assetColumns.impact}
              </th>
              <th className="tw:pb-1 tw:pr-2 tw:font-medium">
                {content.assetColumns.status}
              </th>
              <th className="tw:pb-1 tw:font-medium">
                {content.assetColumns.why}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleAssets.map((entry) => (
              <AssetRow
                key={entry.facility.id}
                entry={entry}
                lang={lang}
                content={content}
              />
            ))}
          </tbody>
        </table>
        {RANKED_ASSETS.length > ASSETS_PREVIEW_COUNT ? (
          <ShowAllToggle
            expanded={showAllAssets}
            total={RANKED_ASSETS.length}
            content={content}
            onToggle={() => setShowAllAssets((prev) => !prev)}
          />
        ) : null}
      </section>

      <section data-tour="japan-sitrep-osint">
        <SectionHeading content={content} id="osint">
          <Badge className="tw:h-4 tw:border-transparent tw:bg-violet-500/20 tw:px-1 tw:text-[10px] tw:text-violet-200">
            {content.osintBadge}
          </Badge>
        </SectionHeading>
        <div className="tw:mb-2 tw:rounded-md tw:border tw:border-border/60 tw:bg-muted/40 tw:p-2">
          <p className="tw:m-0 tw:font-medium">
            {content.keyJudgementsHeading}
          </p>
          <p className="tw:m-0 tw:mt-0.5 tw:leading-snug">
            {content.keyJudgements}
          </p>
        </div>
        <ul className="tw:m-0 tw:list-none tw:space-y-1.5 tw:p-0">
          {visibleOsint.map((item) => (
            <li key={item.id} className="tw:leading-snug">
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                <span className="tw:font-medium">{item.source}</span>
                <span className="tw:text-muted-foreground">
                  {item.sourceType} · {item.time}
                </span>
                <Badge
                  variant="outline"
                  className="tw:h-4 tw:px-1 tw:text-[10px] tw:text-muted-foreground"
                >
                  {content.demoSourceTag}
                </Badge>
              </div>
              <p className="tw:m-0">{item.summary}</p>
            </li>
          ))}
        </ul>
        {content.osint.length > OSINT_PREVIEW_COUNT ? (
          <ShowAllToggle
            expanded={showAllOsint}
            total={content.osint.length}
            content={content}
            onToggle={() => setShowAllOsint((prev) => !prev)}
          />
        ) : null}
      </section>

      <section data-tour="japan-sitrep-partners">
        <SectionHeading content={content} id="partners" />
        <ul className="tw:m-0 tw:grid tw:list-none tw:grid-cols-1 tw:gap-1 tw:p-0 tw:sm:grid-cols-2">
          {content.partners.map((partner) => (
            <li key={partner.id} className="tw:flex tw:items-start tw:gap-1.5">
              <CountryBadge country={partner.country} />
              <span>
                <span className="tw:font-medium">{partner.name}</span>
                <span className="tw:text-muted-foreground">
                  {' '}
                  — {partner.role}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section data-tour="japan-sitrep-response">
        <SectionHeading content={content} id="response" />
        <ol className="tw:m-0 tw:space-y-1.5 tw:pl-4">
          {content.response.map((objective) => (
            <li key={objective.id} className="tw:leading-snug">
              <span>{objective.text}</span>
              <span className="tw:mt-0.5 tw:flex tw:flex-wrap tw:gap-1">
                <Badge
                  variant="outline"
                  className="tw:h-4 tw:px-1 tw:text-[10px]"
                >
                  {objective.lineOfEffort}
                </Badge>
                <Badge
                  variant="outline"
                  className="tw:h-4 tw:px-1 tw:text-[10px] tw:text-muted-foreground"
                >
                  {objective.division}
                </Badge>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
